import {
	Checkbox,
	FormControlLabel,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import type { AnyFieldApi } from "@tanstack/react-form";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import MUITelInput from "../atomics/mui-tel-input";

export type FormFieldInputWrapperProps = {
	field: AnyFieldApi;
	required?: boolean;
	children: React.ReactNode;
};

export const FormFieldInputWrapper = ({
	children,
	field,
	...props
}: FormFieldInputWrapperProps) => {
	const { t } = useTranslation();
	const label = t(`relocate.${field.name}_label`);
	const info = t(`relocate.${field.name}_info`, { defaultValue: "" });

	return (
		<>
			<Stack direction="row" justifyContent="space-between">
				<Typography>
					{label}
					{props.required && (
						<Typography component="span" color="error">
							{" "}
							*
						</Typography>
					)}
				</Typography>
				{!!info && (
					<Typography sx={{ display: { xs: "none", md: "block" } }}>
						{info}
					</Typography>
				)}
			</Stack>
			{children}
			{!!info && (
				<Typography sx={{ display: { md: "none" } }}>{info}</Typography>
			)}
		</>
	);
};

export type BaseFormFieldProps = {
	field: FormFieldInputWrapperProps["field"];
	required?: boolean;
	customErrorMessage?: string;
};

export type FormTextFieldProps = BaseFormFieldProps;

export const FormTextField = ({ field, ...props }: FormTextFieldProps) => {
	const helperText =
		field.state.meta.isTouched &&
		!field.state.meta.isValid &&
		props.customErrorMessage
			? props.customErrorMessage
			: field.state.meta.errors.join("|");
	return (
		<FormFieldInputWrapper field={field} required={props.required}>
			<TextField
				required={props.required}
				value={field.state.value}
				onChange={(e) => field.handleChange(e.target.value)}
				onBlur={field.handleBlur}
				error={!!field.state.meta.errors.length}
				fullWidth
				helperText={helperText}
			/>
		</FormFieldInputWrapper>
	);
};

export type FormNumberFieldProps = BaseFormFieldProps;

export const FormNumberField = ({ field, ...props }: FormNumberFieldProps) => {
	const helperText =
		field.state.meta.isTouched &&
		!field.state.meta.isValid &&
		props.customErrorMessage
			? props.customErrorMessage
			: field.state.meta.errors.join("|");

	return (
		<FormFieldInputWrapper field={field} required={props.required}>
			<TextField
				required={props.required}
				value={field.state.value}
				onChange={(e) => field.handleChange(e.target.value)}
				onBlur={field.handleBlur}
				error={!!field.state.meta.errors.length}
				fullWidth
				helperText={helperText}
			/>
		</FormFieldInputWrapper>
	);
};

export type FormDatePickerFieldProps = BaseFormFieldProps;

export const FormDatePickerField = ({
	field,
	...props
}: FormDatePickerFieldProps) => {
	const helperText =
		field.state.meta.isTouched &&
		!field.state.meta.isValid &&
		props.customErrorMessage
			? props.customErrorMessage
			: field.state.meta.errors.join("|");

	return (
		<FormFieldInputWrapper field={field} required={props.required}>
			<DatePicker
				slotProps={{
					textField: {
						fullWidth: true,
						error: !!field.state.meta.errors.length,
						helperText,
					},
				}}
				name={field.name}
				value={dayjs(field.state.value, "YYYY-MM-DD")}
				onChange={(newDate) => {
					const date = newDate?.format("YYYY-MM-DD") || "";
					field.handleChange(date);
				}}
			/>
		</FormFieldInputWrapper>
	);
};

export type FormTelephoneFieldProps = {
	field: FormFieldInputWrapperProps["field"];
	required?: boolean;
};

export const FormTelephoneField = ({
	field,
	...props
}: FormTelephoneFieldProps) => {
	return (
		<FormFieldInputWrapper field={field} required={props.required}>
			<MUITelInput
				name={field.name}
				error={!!field.state.meta.errors.length}
				value={field.state.value ?? undefined}
				onChange={(newTlf) => {
					field.handleChange(newTlf);
				}}
				fullWidth
			/>
		</FormFieldInputWrapper>
	);
};

export type FormCheckboxFieldProps = {
	field: FormFieldInputWrapperProps["field"];
	required?: boolean;
	disabled?: boolean;
};

export const FormCheckboxField = ({
	field,
	...props
}: FormCheckboxFieldProps) => {
	const { t } = useTranslation();
	const label = t(`relocate.${field.name}_label`);

	return (
		<FormControlLabel
			disabled={props.disabled}
			checked={field.state.value}
			onChange={(_, checked) => {
				field.handleChange(checked);
			}}
			control={<Checkbox />}
			label={label}
		/>
	);
};
